"use client";

import { useState } from "react";
import NextImage from "next/image";
import { cn } from "@/lib/utils";
import ChevronLeft from "lucide-react/dist/esm/icons/chevron-left";
import ChevronRight from "lucide-react/dist/esm/icons/chevron-right";
import Play from "lucide-react/dist/esm/icons/play";
import ZoomIn from "lucide-react/dist/esm/icons/zoom-in";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { getYouTubeEmbedUrl, getYouTubeThumbnail, isValidYouTubeUrl } from "@/lib/video-utils";
import { trackAssetAnalyticsEvent } from "@/lib/analytics/events";

interface AssetImage {
  id: string;
  image_url: string;
  is_primary: boolean;
  display_order: number;
}

interface AssetImageGalleryProps {
  images: AssetImage[];
  videoUrl?: string | null;
  title: string;
  assetId: string;
}

export function AssetImageGallery({ assetId, images, videoUrl, title }: AssetImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Support both YouTube URLs and uploaded video URLs
  const isYouTubeVideo = videoUrl ? isValidYouTubeUrl(videoUrl) : false;
  const isUploadedVideo = videoUrl ? videoUrl.includes("asset-videos") : false;
  const hasValidVideo = isYouTubeVideo || isUploadedVideo;

  // Defensively guard against images being undefined/null (e.g. missing RPC field)
  const safeImages = Array.isArray(images) ? images : [];

  const allMedia = [
    ...safeImages.map((img) => ({ type: "image" as const, ...img })),
    ...(hasValidVideo ? [{ type: "video" as const, id: "video", url: videoUrl!, isYouTube: isYouTubeVideo }] : []),
  ];

  const currentMedia = allMedia[selectedIndex];

  const goToPrevious = () => {
    setSelectedIndex((prev) => (prev === 0 ? allMedia.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setSelectedIndex((prev) => (prev === allMedia.length - 1 ? 0 : prev + 1));
  };

  if (allMedia.length === 0) {
    return (
      <div className="aspect-square bg-muted rounded-lg flex items-center justify-center">
        <p className="text-muted-foreground">No images available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Image/Video - 4:3 aspect ratio for better fit */}
      <div 
        role="button"
        tabIndex={0}
        aria-label={`View ${title} fullscreen`}
        className="relative aspect-[4/3] bg-secondary/30 rounded-xl overflow-hidden cursor-zoom-in group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary border border-border/50"
        onClick={() => setLightboxOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLightboxOpen(true); } }}
      >
        {currentMedia.type === "image" ? (
          <>
            {/* Blurred fill behind the contained image so non-4:3 screenshots
                never show hard letterbox bars — the full image always stays
                visible (object-contain), uncropped. */}
            <NextImage
              src={currentMedia.image_url}
              alt=""
              aria-hidden
              fill
              sizes="64px"
              quality={10}
              className="object-cover scale-110 blur-2xl opacity-50 saturate-150"
            />
            <NextImage
              src={currentMedia.image_url}
              alt={`${title} - Image ${selectedIndex + 1}`}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 60vw, 50vw"
              priority={selectedIndex === 0}
              className="object-contain transition-transform duration-500 group-hover:scale-[1.02]"
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-black" onClick={(e) => e.stopPropagation()}>
            {currentMedia.isYouTube ? (
              <iframe
                src={getYouTubeEmbedUrl(currentMedia.url) || currentMedia.url}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={`${title} video`}
              />
            ) : (
              <video
                src={currentMedia.url}
                controls
                className="w-full h-full object-contain"
                preload="metadata"
              />
            )}
          </div>
        )}

        {/* Zoom hint */}
        <div className="absolute top-3 right-3 bg-background/80 backdrop-blur-sm p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
          <ZoomIn className="h-4 w-4" />
        </div>

        {/* Navigation Arrows */}
        {allMedia.length > 1 && (
          <>
            <Button
              variant="secondary"
              size="icon"
              aria-label="Previous image"
              className="absolute left-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity h-10 w-10 rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              aria-label="Next image"
              className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity h-10 w-10 rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </>
        )}
      </div>

      {/* Thumbnails - Horizontal scroll */}
      {allMedia.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {allMedia.map((media, index) => (
            <button
              key={media.id}
              onClick={() => setSelectedIndex(index)}
              onMouseEnter={() => setSelectedIndex(index)}
              aria-label={`View ${media.type === 'video' ? 'video' : `image ${index + 1}`}`}
              aria-pressed={index === selectedIndex}
              className={cn(
                "relative flex-shrink-0 w-16 h-12 lg:w-20 lg:h-[60px] rounded-lg overflow-hidden border-2 transition-all",
                index === selectedIndex
                  ? "border-primary ring-2 ring-primary/20"
                  : "border-transparent hover:border-muted-foreground/50"
              )}
            >
              {media.type === "image" ? (
                <div className="relative w-full h-full">
                  <NextImage
                    src={media.image_url}
                    alt={`Thumbnail ${index + 1}`}
                    fill
                    sizes="80px"
                    className="object-cover rounded-md"
                  />
                </div>
              ) : (
                <div className="relative w-full h-full bg-muted flex items-center justify-center overflow-hidden rounded-md">
                  {media.isYouTube && getYouTubeThumbnail(media.url) ? (
                    <div className="relative w-full h-full">
                      <NextImage
                        src={getYouTubeThumbnail(media.url)!}
                        alt="Video thumbnail"
                        fill
                        sizes="80px"
                        className="object-cover"
                      />
                    </div>
                  ) : !media.isYouTube ? (
                    <video src={media.url} className="w-full h-full object-cover" preload="metadata" muted />
                  ) : null}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Play className="h-5 w-5 text-white fill-white" />
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      <Dialog
        open={lightboxOpen}
        onOpenChange={(open) => {
          setLightboxOpen(open);
          if (open) {
            const previewType =
              currentMedia.type === "image" ? "image" : "video";
            trackAssetAnalyticsEvent({
              name: "asset_preview_opened",
              payload: { asset_id: assetId, preview_type: previewType },
            });
          }
        }}
      >
        <DialogContent className="max-w-6xl p-0 bg-black/95 border-0">
          <div className="relative min-h-[60dvh] flex items-center justify-center p-4">
            {currentMedia.type === "image" ? (
              <div className="relative w-full" style={{ maxHeight: "85vh", aspectRatio: "16/9" }}>
                <NextImage
                  src={currentMedia.image_url}
                  alt={`${title} - Image ${selectedIndex + 1}`}
                  fill
                  sizes="(max-width: 1200px) 100vw, 80vw"
                  className="object-contain"
                />
              </div>
            ) : (
              currentMedia.isYouTube ? (
                <iframe
                  src={getYouTubeEmbedUrl(currentMedia.url) || currentMedia.url}
                  className="w-full aspect-video max-h-[85dvh]"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={`${title} video`}
                />
              ) : (
                <video
                  src={currentMedia.url}
                  controls
                  className="w-full aspect-video max-h-[85dvh] object-contain"
                  preload="metadata"
                />
              )
            )}

            {allMedia.length > 1 && (
              <>
                <Button
                  variant="secondary"
                  size="icon"
                  aria-label="Previous image"
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full"
                  onClick={goToPrevious}
                >
                  <ChevronLeft className="h-6 w-6" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  aria-label="Next image"
                  className="absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full"
                  onClick={goToNext}
                >
                  <ChevronRight className="h-6 w-6" />
                </Button>
              </>
            )}

            {/* Image counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-background/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm">
              {selectedIndex + 1} / {allMedia.length}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
